import {
  Box,
  Flex,
  FormControl,
  Heading,
  HStack,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { HiOutlineDocumentSearch } from "react-icons/hi";

import {
  LoadingIndicator,
  SearchInput,
  SortableTable,
  SortableTableColumn,
} from "../../../../components";
import { EmptyStateWithIcon } from "../../../../components/EmptyStates";
import useOffsetPagination from "../../../../hooks/useOffsetPagination";
import { formatDate } from "../../../../utils/datetime";
import {
  OrgClientsPaginatedQuery,
  useOrgClientsPaginatedLazyQuery,
  useUpdateClientAutoJoinMutation,
} from "../../../graphql";
import RecordingSettingsPopover from "./RecordingSettingsPopover";
import { SettingSwitchWithConfirmDialogue } from "./SettingSwitchWithConfirmDialogue";

type Client = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<OrgClientsPaginatedQuery["currentUser"]>["organization"]
      >["clientsPag"]
    >["results"]
  >[number]
>;

const ClientRecordingSettings: React.FC = () => {
  const toast = useToast();
  const [getClients, { data, loading }] = useOrgClientsPaginatedLazyQuery({
    fetchPolicy: "network-only",
    onError: (err) => {
      toast({
        title: "Error",
        description: `Failed to load departments: ${err.message}`,
        status: "error",
      });
    },
    onCompleted: ({ currentUser }) => {
      if (currentUser?.organization?.clientsPag) {
        const clientList = currentUser.organization.clientsPag;
        setNumberOfPages(
          currentUser.organization.clientsPag.pageInfo?.totalPages || 1
        );
        setListItems(clientList.results ?? []);
      }
    },
  });

  const {
    listItems: clients,
    pageOptions,
    setSearchTerm,
    setListItems,
    setNumberOfPages,
    searchTerm,
    reload,
  } = useOffsetPagination<Client>(getClients, {
    initialSortBy: { id: "createdAt", desc: true },
  });

  const atsOptInEnabled =
    data?.currentUser?.organization?.atsOptInEnabled ?? false;

  const [updateClientAutoJoin] = useUpdateClientAutoJoinMutation({
    onError: (err) => {
      toast({
        status: "error",
        title: "Error",
        description: `Failed to turn on Auto Join New Positions: ${err.message}`,
      });
    },
    onCompleted: (data) => {
      if (
        data?.updateClientAutoJoin &&
        data?.updateClientAutoJoin.clients.length > 0
      ) {
        toast({
          title: "Success",
          description: `Successfully updated recording settings for the ${data?.updateClientAutoJoin.clients[0].name} position group`,
          status: "success",
        });
        reload();
      }
    },
  });

  const handleUpdateClientAutoJoin = (
    clients: Client[],
    autoJoinInterview: boolean,
    optIn: boolean
  ): Promise<any> => {
    const clientIds = clients.map((c) => c.id);
    return updateClientAutoJoin({
      variables: { clientIds, autoJoinInterview, optIn },
    });
  };

  const columns: SortableTableColumn<Client>[] = [
    {
      Header: "Name",
      accessor: "name",
      id: "name",
    },
    {
      Header: "Added",
      accessor: "createdAt",
      Cell: ({ row: { original: client } }: { row: { original: Client } }) => (
        // cspell: disable-next-line
        <Text style={{ fontVariantNumeric: "tabular-nums" }} maxW="102px">
          {formatDate(client.createdAt, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
          })}
        </Text>
      ),
    },
    {
      Header: "Record",
      accessor: "autoJoinAllNewPositions",
      Cell: ({ row: { original: client } }: { row: { original: Client } }) => {
        const [loading, setLoading] = useState(false);
        return (
          <HStack align="baseline" spacing="3" maxW="120px">
            {loading ? (
              <LoadingIndicator
                size="xs"
                alignSelf="flex-start"
                width="inherit"
              />
            ) : (
              <FormControl>
                <Switch
                  isChecked={client.autoJoinAllNewPositions}
                  colorScheme="prism"
                  display="flex"
                  alignItems="center"
                  fontSize="sm"
                  lineHeight={1.5}
                  onChange={(e) => {
                    setLoading(true);
                    const newValue = e.target.checked;
                    handleUpdateClientAutoJoin(
                      [client],
                      newValue,
                      client.atsOptInAllNewPositions
                    )
                      .then(() => {
                        setLoading(false);
                      })
                      .catch(() => {
                        setLoading(false);
                      });
                  }}
                >
                  {client.autoJoinAllNewPositions
                    ? "Auto-join enabled"
                    : "Auto-join disabled"}
                </Switch>
              </FormControl>
            )}
          </HStack>
        );
      },
    },
    {
      Header: "Positions",
      accessor: "positionsCount",
    },
    {
      Header: "Bulk Actions",
      Cell: ({ row: { original: client } }: { row: { original: Client } }) => {
        return (
          <HStack mb="3">
            <RecordingSettingsPopover
              searchTerm={null}
              clientId={client.id}
              locationId={null}
              positionsCount={client.positionsCount ?? 0}
              disabled={!client.positionsCount}
              showOptIn={atsOptInEnabled}
            />
          </HStack>
        );
      },
    },
  ];

  if (atsOptInEnabled) {
    const optInColumn: SortableTableColumn<Client> = {
      Header: "Consent",
      accessor: "atsOptInAllNewPositions",
      Cell: ({ row: { original: client } }: { row: { original: Client } }) => {
        return (
          <SettingSwitchWithConfirmDialogue
            isChecked={client.atsOptInAllNewPositions}
            onToggle={(checked) => {
              return handleUpdateClientAutoJoin(
                [client],
                client.autoJoinAllNewPositions,
                checked
              );
            }}
            shouldConfirm={!client.atsOptInAllNewPositions}
            headerText="Are you sure you want to enable opt-in consent for all future positions?"
            headerBody={
              <>
                Enabling <strong>Opt In consent</strong> will immediately apply
                opt-in rules to all active candidates. Any candidate not
                currently assigned to an opt-in position will be automatically
                marked as Opted Out, which may disrupt scheduling and generate a
                large volume of opt-out notification emails. This change may be
                difficult to reverse.
              </>
            }
            confirmButtonText="Proceed Anyways"
          >
            {client.atsOptInAllNewPositions
              ? "Opt-in consent"
              : "Opt-out consent"}
          </SettingSwitchWithConfirmDialogue>
        );
      },
    };
    columns.splice(columns.length - 3, 0, optInColumn);
  }

  return (
    <Box w="fit-content" mt="4">
      <Heading as="h1" size="md" mb="4">
        Department Recording Settings
      </Heading>
      <Box>
        <Flex
          alignItems="left"
          mb={5}
          flexFlow="column nowrap"
          id="search-filter--search"
        >
          <SearchInput
            placeholder="Search departments by name"
            defaultValue={searchTerm}
            onSearch={setSearchTerm}
            width="508px"
            maxWidth="508px"
          />
        </Flex>
      </Box>
      {loading || clients.length > 0 ? (
        <SortableTable<Client>
          columns={columns}
          columnWidths={["102px", "50px", "102px", "100px", "100px"]}
          data={clients}
          autoResetSortBy={false}
          initialSort={{ id: "createdAt", desc: true }}
          pageOptions={pageOptions}
          manualPagination
          width="1200px"
          loading={loading}
        />
      ) : (
        <EmptyStateWithIcon
          maxWidth="1200px"
          heading="No departments found"
          icon={HiOutlineDocumentSearch}
          Text={() => (
            <Box>
              No departments were found{" "}
              {searchTerm
                ? " that match your query. Please try something else."
                : "."}
            </Box>
          )}
          padded={false}
        />
      )}
    </Box>
  );
};

export default ClientRecordingSettings;
